import { useEffect, useRef, useState } from 'react';

interface StaffSelectorProps {
  staff: any[];
  selectedStaffIds: string[];
  onStaffChange: (ids: string[]) => void;
  multiSelect: boolean;
  placeholder: string;
  loading: boolean;
}

const STAFF_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

export default function StaffSelector({
  staff,
  selectedStaffIds,
  onStaffChange,
  multiSelect,
  placeholder,
  loading,
}: StaffSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!loading) {
      setIsOpen(!isOpen);
    }
  };

  const handleStaffClick = (staffId: string) => {
    if (multiSelect) {
      const newSelection = selectedStaffIds.includes(staffId)
        ? selectedStaffIds.filter((id) => id !== staffId)
        : [...selectedStaffIds, staffId];
      onStaffChange(newSelection);
    } else {
      onStaffChange([staffId]);
      setIsOpen(false);
    }
  };

  const handleSelectAll = () => {
    onStaffChange(staff.map((s) => s.id));
  };

  const handleClear = () => {
    onStaffChange([]);
  };

  const getStaffColor = (staffMember: any, index: number) => {
    return staffMember.color || STAFF_COLORS[index % STAFF_COLORS.length];
  };

  const getButtonText = () => {
    if (loading) {
      return 'Loading...';
    }
    if (selectedStaffIds.length === 0) {
      return placeholder;
    }
    if (selectedStaffIds.length === 1) {
      const selectedStaff = staff.find((s) => s.id === selectedStaffIds[0]);
      return selectedStaff?.name || placeholder;
    }
    return `${selectedStaffIds.length} selected`;
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading}
        className={`
          w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-lg
          hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500
          flex items-center justify-between
          ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <span className={selectedStaffIds.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
          {getButtonText()}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {/* Controls (only show for multi-select) */}
          {multiSelect && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
              <button
                type="button"
                onClick={handleClear}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Select All
              </button>
            </div>
          )}

          {/* Staff list */}
          <div className="py-1">
            {staff.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">No staff members available</div>
            ) : (
              staff.map((staffMember, index) => {
                const isSelected = selectedStaffIds.includes(staffMember.id);
                const color = getStaffColor(staffMember, index);

                return (
                  <button
                    key={staffMember.id}
                    type="button"
                    onClick={() => handleStaffClick(staffMember.id)}
                    className={`
                      w-full px-4 py-2 text-left hover:bg-gray-50
                      flex items-center gap-3
                      ${isSelected ? 'bg-blue-50' : ''}
                    `}
                  >
                    {/* Checkbox or Radio */}
                    {multiSelect ? (
                      <div
                        className={`
                          w-4 h-4 border rounded flex items-center justify-center flex-shrink-0
                          ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}
                        `}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    ) : (
                      <div
                        className={`
                          w-4 h-4 border rounded-full flex items-center justify-center flex-shrink-0
                          ${isSelected ? 'border-blue-600' : 'border-gray-300'}
                        `}
                      >
                        {isSelected && <div className="w-2 h-2 bg-blue-600 rounded-full" />}
                      </div>
                    )}

                    {/* Color dot */}
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />

                    {/* Name */}
                    <span className="text-sm text-gray-900 flex-1">{staffMember.name}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
